import { SmartChargeSettingsFormModel, SmartChargeSettingsModel } from '../models'
import { useContext, useEffect, useState } from 'react'
import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { isUndefined } from 'lodash'
import { getSettings as getSettingsApi, updateSettings as updateSettingsApi } from 'src/Libs/smart-charge/api/smart-charge.api'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { InstallationOnlineStatusContext } from 'src/Libs/installations/context/installation-online-status-context'
import { useFarmContext } from 'src/Elements/Farm'

interface UseSmartChargeHook {
  isLoading: boolean
  isError: boolean
  loadSettings: () => Promise<void>
  updateSettings: (model: SmartChargeSettingsFormModel) => Promise<void>
  smartChargeSettings: SmartChargeSettingsModel | null
}

/**
 * Provide utils to access the data of the current [Smart Charge Settings API](storages/{sn}/smart-functions/smart-charge)
 * This is the preferred process to access and change smart charge settings for an installation.
 *
 * There is also a legacy api support, which can be accessed through the `useSmartChargeLegacy`-hook.
 *
 * @param storageSerialNr
 */
const useSmartCharge = (storageSerialNr?: string): UseSmartChargeHook => {
  const authFetch = useAuthFetchWithResponse()
  const { enqueueErrorStack } = useSnackbar()
  const { t } = useCustomTranslation()
  const [smartChargeSettings, setSmartChargeSettings] = useState<SmartChargeSettingsModel | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isError, setIsError] = useState(false)
  const { isServerOnline } = useContext(InstallationOnlineStatusContext)
  const { isFarm } = useFarmContext()
  const isServerOffline = isFarm && isServerOnline !== true

  const loadSettings = async (): Promise<void> => {
    if (isUndefined(storageSerialNr)) {
      throw new Error('missing storageSerialNr ')
    }

    const result = await getSettingsApi(storageSerialNr, authFetch, !isServerOffline)
    if (!result.hasErrors) {
      setSmartChargeSettings(result.value)
    }
  }

  const updateSettings = async (model: SmartChargeSettingsFormModel): Promise<void> => {
    if (isUndefined(storageSerialNr)) {
      throw new Error(`Invalid activeStorageId  (id: ${storageSerialNr})`)
    }
    return updateSettingsApi(storageSerialNr, model, authFetch).then(() => {
      if (smartChargeSettings) {
        setSmartChargeSettings({
          ...smartChargeSettings,
          emsWeatherRegulatedChargeEnabled: model.emsWeatherRegulatedChargeEnabled,
          weatherForecastActive: model.weatherForecastActive,
          weatherCalcMode: model.weatherCalcMode,
          emsManualChargeActive: model.emsStartManualCharge > 0 ? true : false,
        })
      }
    })
  }

  useEffect(() => {
    setIsLoading(true)
    loadSettings()
      .catch((error: unknown) => {
        setIsError(true)
        console.error(error)
        enqueueErrorStack(t('settings.smartCharge.load.error'))
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, []) // eslint-disable-line

  return { isLoading, isError, loadSettings, updateSettings, smartChargeSettings }
}

export default useSmartCharge
